import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { NewbuildingBrokerSelfRequestFormBanner } from './NewbuildingBrokerSelfRequestFormBanner';
import { useRegion } from './hooks';
import { trackBannerClick, trackBannerShow } from './tracking';
import { NewbuildingBrokerFlightBanner } from '../NewbuildingBrokerFlightBanner';
import { requestAuthentication } from 'shared/actions/authentication';
import { Dispatch } from 'shared/common/state';
import { NewbuildingBrokerModalsContext } from 'shared/containers/NewbuildingBrokerModalsProvider';
import { selectSource } from 'shared/selectors';
import {
  selectNewbuildingBrokerConsultantBannerButtonText,
  selectNewbuildingBrokerFlightEnabled,
} from 'shared/selectors/newbuildingBrokerConsultantBanner';
import { selectFirstGaLabel } from 'shared/selectors/selectFirstGaLabel';
import { getUser } from 'shared/selectors/user';
import { IOpenSelfRequestModalParams } from 'shared/types/microfrontends';

export const NewbuildingBrokerSelfRequestFormBannerContainer: React.FC = () => {
  const dispatch: Dispatch = useDispatch();

  const buttonText = useSelector(selectNewbuildingBrokerConsultantBannerButtonText);
  const user = useSelector(getUser);
  const region = useRegion();
  const gaLabel = useSelector(selectFirstGaLabel);
  const source = useSelector(selectSource);
  const isBrokerFlightEnabled = useSelector(selectNewbuildingBrokerFlightEnabled);

  const { isSelfRequestModalLoading, startMicrofrontOpening } = React.useContext(NewbuildingBrokerModalsContext);

  // Реф нужен, чтобы получать свежего user в коллбэке во избежание проблемы с замыканием на старое значение.
  const userRef = React.useRef(user);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  const handleButtonClick = React.useCallback(() => {
    trackBannerClick();

    if (!userRef.current.isAuthenticated) {
      dispatch(
        requestAuthentication({
          onSuccess: () => () =>
            startMicrofrontOpening({
              type: 'selfRequestModal',
              params: {
                user: userRef.current,
                region,
                gaLabel,
                source,
              } as IOpenSelfRequestModalParams,
            }),
        }),
      );

      return;
    }

    startMicrofrontOpening({
      type: 'selfRequestModal',
      params: {
        user: userRef.current,
        region,
        gaLabel,
        source,
      } as IOpenSelfRequestModalParams,
    });
  }, [dispatch, region, gaLabel, source, startMicrofrontOpening]);

  if (isBrokerFlightEnabled) {
    return (
      <ActionAfterViewObserver callback={trackBannerShow}>
        <NewbuildingBrokerFlightBanner
          buttonText={buttonText}
          buttonLoading={isSelfRequestModalLoading}
          onClick={handleButtonClick}
        />
      </ActionAfterViewObserver>
    );
  }

  return (
    <ActionAfterViewObserver callback={trackBannerShow}>
      <NewbuildingBrokerSelfRequestFormBanner
        buttonText={buttonText}
        buttonLoading={isSelfRequestModalLoading}
        onClick={handleButtonClick}
      />
    </ActionAfterViewObserver>
  );
};
